import type { Doc } from "building-types/doc";
import { isIsolatedFoundationPatternEdgeBehavior } from "building-types/edgeBehavior/edgeBehavior";
import { EdgeBehaviorType } from "building-types/edgeBehavior/edgeBehaviorTypesEnum";
import { FoundationSectionSizeType } from "building-types/foundations/foundationEnums";
import type { FoundationEdgeOverridesTable } from "building-types/foundations/foundationSystemTypes";
import { SettingsType } from "building-types/settings/settingsType";
import { docGetEntityName } from "./docGetEntityName";
import {
  formatMandatoryReferences,
  type MandatoryReference,
  pushMandatoryReference,
} from "./docMandatoryReferenceUtils";
import { DocEntityTransactErrorNotification } from "./entity/docEntityTransactErrorNotification";

function foundationAssemblyIdMatches(value: string | undefined | null, assemblyId: string): boolean {
  return value === assemblyId;
}

function foundationEdgeOverridesReferenceAssembly(
  overrides: FoundationEdgeOverridesTable | undefined,
  assemblyId: string,
): boolean {
  if (!overrides) {
    return false;
  }

  for (const override of Object.values(overrides)) {
    if (foundationAssemblyIdMatches(override.foundationAssemblyId, assemblyId)) {
      return true;
    }
  }

  return false;
}

/**
 * Yields every entity that holds a mandatory reference to the given foundation
 * assembly. The same entity may be yielded more than once (e.g. a foundation
 * referenced via both its assembly id and an edge override); callers that need a
 * unique list should de-duplicate via `pushMandatoryReference`.
 *
 * Scans doc tables directly so this can be used from docEntityIsDeletable without
 * calling DocEntityNode reverse-reference helpers (which depend on entity config).
 */
function* iterateFoundationAssemblyMandatoryReferences(doc: Doc, assemblyId: string): Generator<MandatoryReference> {
  for (const settingsId in doc.settings) {
    const settings = doc.settings[settingsId];
    if (settings.type !== SettingsType.FOUNDATION_SETTINGS) {
      continue;
    }

    const foundationHash = settings.foundationHash;
    if (!foundationHash) {
      continue;
    }

    const subSystemSettings = settings[foundationHash];
    if (!subSystemSettings) {
      continue;
    }

    if (foundationAssemblyIdMatches(subSystemSettings.foundationAssembly, assemblyId)) {
      yield {
        entityType: "SETTINGS",
        entityId: settingsId,
        entityName: docGetEntityName(doc, { type: "SETTINGS", id: settingsId }),
      };
    }
  }

  for (const columnId in doc.columns) {
    const column = doc.columns[columnId];
    if (foundationAssemblyIdMatches(column.isolatedAssemblyId, assemblyId)) {
      yield { entityType: "COLUMN", entityId: columnId, entityName: column.name };
    }
  }

  for (const edgeBehaviorId in doc.edgeBehaviors) {
    const edgeBehavior = doc.edgeBehaviors[edgeBehaviorId];
    if (isIsolatedFoundationPatternEdgeBehavior(edgeBehavior)) {
      const foundationPattern = edgeBehavior.foundationPattern;
      if (
        foundationPattern.patternType === FoundationSectionSizeType.ISOLATED &&
        foundationAssemblyIdMatches(foundationPattern.assemblyId, assemblyId)
      ) {
        yield { entityType: "EDGE_BEHAVIOR", entityId: edgeBehaviorId, entityName: edgeBehavior.name };
      }
    }

    if (edgeBehavior.edgeBehaviorType === EdgeBehaviorType.FOUNDATION_ASSEMBLY) {
      for (const override of edgeBehavior.foundationAssemblyOverrides) {
        if (foundationAssemblyIdMatches(override.assemblyId, assemblyId)) {
          // The reference is reported at edge-behavior granularity, so a single
          // matching override is enough; remaining overrides would only produce
          // the same (de-duplicated) edge-behavior reference.
          yield { entityType: "EDGE_BEHAVIOR", entityId: edgeBehaviorId, entityName: edgeBehavior.name };
          break;
        }
      }
    }
  }

  for (const floorId in doc.floors) {
    const floor = doc.floors[floorId];
    if (foundationAssemblyIdMatches(floor.foundationAssemblyId, assemblyId)) {
      yield { entityType: "FLOOR", entityId: floorId, entityName: floor.name };
    }
  }

  for (const foundationId in doc.foundations) {
    const foundation = doc.foundations[foundationId];
    if (foundationAssemblyIdMatches(foundation.foundationAssemblyId, assemblyId)) {
      yield { entityType: "FOUNDATION", entityId: foundationId, entityName: foundation.name };
    }

    if (foundationEdgeOverridesReferenceAssembly(foundation.foundationEdgeOverrides, assemblyId)) {
      yield { entityType: "FOUNDATION", entityId: foundationId, entityName: foundation.name };
    }
  }
}

/**
 * Returns true as soon as any entity mandatorily references the given foundation
 * assembly, short-circuiting the scan. Use this when only existence matters
 * (e.g. docEntityIsDeletable) to avoid building a reference list that is discarded.
 */
export function docFoundationAssemblyHasMandatoryReferences(doc: Doc, assemblyId: string): boolean {
  return !iterateFoundationAssemblyMandatoryReferences(doc, assemblyId).next().done;
}

/**
 * Finds entities with mandatory references to the given foundation assembly.
 * Deletion must be blocked while any are returned so FK fields are not left dangling.
 */
export function docFoundationAssemblyMandatoryReferences(doc: Doc, assemblyId: string): readonly MandatoryReference[] {
  const refs: MandatoryReference[] = [];
  const seenReferenceKeys = new Set<string>();

  for (const ref of iterateFoundationAssemblyMandatoryReferences(doc, assemblyId)) {
    if (!ref.entityId) {
      continue;
    }

    pushMandatoryReference(refs, seenReferenceKeys, ref.entityType, ref.entityId, ref.entityName);
  }

  return refs.map(({ entityType, entityName }) => ({ entityType, entityName }));
}

export function docGuardFoundationAssemblyDeletion(doc: Doc, assemblyId: string): void {
  const mandatoryRefs = docFoundationAssemblyMandatoryReferences(doc, assemblyId);
  if (mandatoryRefs.length > 0) {
    throw new DocEntityTransactErrorNotification(
      `Cannot delete Foundation Assembly: it is still referenced by ${formatMandatoryReferences(mandatoryRefs)}.`,
      "FOUNDATION_ASSEMBLY",
    );
  }
}
